import React, { useRef, useEffect, useMemo } from 'react';
import { Layer, Image as KonvaImage, Rect } from 'react-konva';
import useImage from 'use-image';
import type Konva from 'konva';
import { debounce } from 'lodash-es';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useStableCallback, useRenderLoopDetection } from '../../hooks/hookUtils';

export interface FogSettings {
  enabled: boolean;
  data: string | null;
  eraserSize: number;
}

interface KonvaFogLayerProps {
  imageRect: { x: number; y: number; width: number; height: number; };
  containerSize: { width: number; height: number; } | null;
  fogSettings: FogSettings;
  onSaveFog: (dataUrl: string) => void;
}

const KonvaFogLayer: React.FC<KonvaFogLayerProps> = ({ imageRect, containerSize, fogSettings, onSaveFog }) => {
  // Development-time render loop detection for this canvas-based component
  useRenderLoopDetection('KonvaFogLayer');

  const [fogImage] = useImage(fogSettings?.data ?? '', 'anonymous');
  const imageNodeRef = useRef<Konva.Image | null>(null);
  const rectNodeRef = useRef<Konva.Rect | null>(null);
  const imageRef = useRef<Konva.Shape | null>(null);
  const isErasingRef = useRef(false); // True when mouse is down and erasing
  const hasChangedRef = useRef(false);

  // useStableCallback ensures saveFogState always accesses the latest onSaveFog
  // without being recreated when it changes. This is critical because
  // the debounced save function depends on this callback, and recreating it would
  // cancel pending saves and create a new debounce timer.
  const saveFogState = useStableCallback(() => {
    if (imageRef.current && hasChangedRef.current) {
      const dataUrl = imageRef.current.toDataURL();
      onSaveFog(dataUrl);
      hasChangedRef.current = false;
    }
  });

  // Inlining the debounce logic to simplify type inference
  const debouncedSave = useMemo(() => {
    return debounce(saveFogState, 1000);
  }, [saveFogState]);

  // Cleanup the debounced function on unmount
  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);

  // Effect to initialize the fog canvas when it's first enabled or data is cleared
  useEffect(() => {
    if (fogSettings?.enabled && !fogSettings?.data && imageRef.current) {
      const canvas = imageRef.current.getCanvas();
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        imageRef.current.getLayer()?.batchDraw();
        hasChangedRef.current = true;
        debouncedSave.flush(); // Use flush to save immediately on initialization
      }
    }
  }, [fogSettings?.enabled, fogSettings?.data, debouncedSave]);

  const erase = (stage: Konva.Stage) => {
    if (!isErasingRef.current || !fogSettings?.enabled || !imageRef.current) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    // We need to transform the pointer position from stage coordinates to image coordinates
    const transform = imageRef.current.getAbsoluteTransform().copy().invert();
    const transformedPointer = transform.point(pointer);

    const canvas = imageRef.current.getCanvas();
    const ctx = canvas.getContext();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(transformedPointer.x, transformedPointer.y, fogSettings.eraserSize, 0, Math.PI * 2, false);
    (ctx ?? []).fill();

    // Redraw the layer to show the change
    imageRef.current.getLayer()?.batchDraw();
    hasChangedRef.current = true;
    debouncedSave();
  };

  const handleMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    if (!fogSettings?.enabled) return;
    isErasingRef.current = true;
    const stage = e.target.getStage();
    if (stage) {
      erase(stage);
    }
  };

  const handleMouseUp = () => {
    if (isErasingRef.current) {
      isErasingRef.current = false;
      debouncedSave.flush(); // Immediately save the final state on mouse up
    }
  };

  const handleMouseMove = (e: KonvaEventObject<MouseEvent>) => {
    if (isErasingRef.current) {
      const stage = e.target.getStage();
      if (stage) {
        erase(stage);
      }
    }
  };

  // This effect keeps the generic `imageRef` pointing to the currently rendered Konva node.
  useEffect(() => {
    imageRef.current = imageNodeRef.current ?? rectNodeRef.current;
  });

  if (!fogSettings?.enabled || !containerSize) {
    return null;
  }

  return (
    <Layer
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseUp} // Stop erasing if mouse leaves stage
    >
      {fogSettings.data ? (
        <KonvaImage
          ref={imageNodeRef}
          image={fogImage}
          {...imageRect}
          listening={false} // Don't listen for events on the image itself
        />
      ) : (
        // If no data, render a black rectangle and use its canvas for drawing
        <Rect ref={rectNodeRef} {...imageRect} fill="black" listening={false} />
      )}
    </Layer>
  );
};

export default KonvaFogLayer;
